
import axios from 'utils/axios';
import { generateLocalId } from 'utils/randomId';

/** Known API / UI values for `eventType` (display). Wire field is `type` (e.g. `ADMIN`, `PROMOTIONAL`). */
export const EVENT_TYPE_VALUES = ['admin', 'sports', 'system', 'promotional', 'other'] as const;
export type EventType = (typeof EVENT_TYPE_VALUES)[number];

const EVENT_TYPE_TO_API: Record<EventType, string> = {
  admin: 'ADMIN',
  sports: 'SPORTS',
  system: 'SYSTEM',
  promotional: 'PROMOTIONAL',
  other: 'OTHER',
};

/** Maps UI event type slug → API `type` wire value. */
export function mapEventTypeToApiType(eventType: EventType): string {
  return EVENT_TYPE_TO_API[eventType] ?? 'OTHER';
}

const API_BASE_URL = '/api/admin/event-announcements';
export interface ZipRange {
  zipFrom: string;
  zipTo: string;
}

export interface EventNotification {
  id: string;
  notificationTitle: string;
  notificationBody: string;
  pushTitle?: string;
  pushBody?: string;
  eventType: EventType;
  /** When `"event"`, item is excluded from the admin Event announcements page. */
  feedItemType?: string;
  isRead: boolean;
  scheduledSendCount?: number;
  scheduledSendTimes?: string[];
  targetAll: boolean;
  zipRange?: ZipRange;
  pushSentAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNotificationPayload {
  title: string;
  description: string;
  eventType: EventType;
  isRead?: boolean;
  scheduledSendCount?: number;
  scheduledSendTimes?: string[];
  targetAll?: boolean;
  zipRange?: ZipRange;
}

export type UpdateNotificationPayload = Partial<CreateNotificationPayload>;

export interface SendPushPayload {
  notificationId: string;
  targetAll: boolean;
  zipRange?: ZipRange;
  title?: string;
  bodyPreview?: string;
}

export interface PushHistoryEntry {
  id: string;
  notificationId: string;
  title: string;
  bodyPreview: string;
  sentAt: string;
  targetAll: boolean;
  zipRange?: ZipRange;
}

export interface EventAnnouncementApiRow {
  _id?: string;
  id?: string;
  notificationTitle?: string;
  notification_title?: string;
  notificationBody?: string;
  notification_body?: string;
  title?: string;
  message?: string;
  description?: string;
  body?: string;
  summary?: string;
  eventType?: string;
  event_type?: string;
  type?: string;
  status?: string;
  targetAll?: boolean;
  target_all?: boolean;
  zipRange?: { zipFrom?: string; zipTo?: string };
  zip_range?: { zip_from?: string; zip_to?: string; zipFrom?: string; zipTo?: string };
  zip3From?: string;
  zip3To?: string;
  zipPrefixFrom?: string;
  zip_prefix_from?: string;
  zipPrefixTo?: string;
  zip_prefix_to?: string;
  zip_from?: string;
  zip_to?: string;
  pushSentAt?: string;
  push_sent_at?: string;
  pushTitle?: string;
  push_title?: string;
  pushBody?: string;
  push_body?: string;
  isRead?: boolean;
  is_read?: boolean;
  audienceScope?: string;
  audience_scope?: string;
  category?: string;
  scheduledSendCount?: number;
  scheduled_send_count?: number;
  sendCount?: number;
  send_count?: number;
  scheduledSendAt?: string;
  scheduled_send_at?: string;
  sendAt?: string;
  send_at?: string;
  scheduledSendTimes?: unknown;
  scheduled_send_times?: unknown;
  /** Alternate API shape: array of ISO strings or `{ at }` objects */
  scheduledSends?: unknown;
  /** Backend field for automated push instants (ISO strings). */
  pushScheduleTimes?: unknown;
  push_schedule_times?: unknown;
  pushScheduleCursor?: number;
  push_schedule_cursor?: number;
  feedItemType?: string;
  feed_item_type?: string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

export interface EventAnnouncementsListResponse {
  success?: boolean;
  message?: string;
  data?: EventAnnouncementApiRow[];
  announcements?: EventAnnouncementApiRow[];
  items?: EventAnnouncementApiRow[];
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
  };
  total?: number;
}

export type ListEventAnnouncementsParams = {
  page?: number;
  limit?: number;
  search?: string;
  feedItemType?: string;
};

export type ListEventAnnouncementsResult = {
  items: EventNotification[];
  total: number;
};

const STORAGE_KEY = 'admin_notifications';
export const ADMIN_PUSH_SENT = 'admin-push-sent';

/** API feed classification for admin-created announcements (wire `feedItemType`). */
const API_FEED_ITEM_TYPE_ADMIN = 'admin';

export type AdminPushSentDetail = { entry: PushHistoryEntry };

const ZIP3 = /^\d{3}$/;
/** US ZIP prefix / full code for `ZIP_PREFIX_RANGE` (e.g. 10001–10003). */
const ZIP_PREFIX = /^\d{3,5}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isValidZip3(value: string): boolean {
  return ZIP3.test(value.trim());
}

export function isValidZipPrefix(value: string): boolean {
  return ZIP_PREFIX.test(value.trim());
}

function generateId(): string {
  return generateLocalId();
}

function normalizeNotification(raw: EventNotification): EventNotification {
  return {
    ...raw,
    isRead: raw.isRead === true,
  };
}

function load(): EventNotification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as EventNotification[];
      return parsed.map((n) => normalizeNotification(n));
    }
    return [];
  } catch {
    return [];
  }
}

function save(items: EventNotification[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function extractNestedAnnouncementRows(data: Record<string, unknown>): EventAnnouncementApiRow[] {
  if (Array.isArray(data.announcements)) return data.announcements;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.rows)) return data.rows;
  return [];
}

function announcementLoadErrorMessage(payload: Record<string, unknown>): string {
  if (typeof payload.message === 'string') {
    return payload.message;
  }
  return 'Failed to load event announcements';
}

function extractAnnouncementRowsFromRecord(payload: Record<string, unknown>): EventAnnouncementApiRow[] {
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.announcements)) return payload.announcements;
  if (Array.isArray(payload.items)) return payload.items;
  if (isRecord(payload.data)) {
    return extractNestedAnnouncementRows(payload.data);
  }
  return [];
}

function extractAnnouncementRows(payload: unknown): EventAnnouncementApiRow[] {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload as EventAnnouncementApiRow[];
  if (!isRecord(payload)) return [];
  if (payload.success === false) {
    throw new Error(announcementLoadErrorMessage(payload));
  }
  return extractAnnouncementRowsFromRecord(payload);
}

function pickTotalFromMeta(meta: unknown): number | null {
  if (!isRecord(meta)) return null;
  const t = meta.total ?? meta.totalCount ?? meta.count ?? meta.totalItems;
  if (typeof t === 'number' && Number.isFinite(t)) return Math.max(0, Math.floor(t));
  if (typeof t === 'string' && /^\d+$/.test(t)) return Math.max(0, Number.parseInt(t, 10));
  return null;
}

function extractAnnouncementTotal(payload: unknown, itemsLength: number): number {
  if (!isRecord(payload)) return itemsLength;
  const fromTop = pickTotalFromMeta(payload.meta);
  if (fromTop != null) return fromTop;
  const topTotal = payload.total ?? payload.count;
  if (typeof topTotal === 'number' && Number.isFinite(topTotal)) return Math.max(0, Math.floor(topTotal));
  const data = payload.data;
  if (isRecord(data)) {
    const nested = pickTotalFromMeta(data.meta);
    if (nested != null) return nested;
    const innerTotal = data.total ?? data.count;
    if (typeof innerTotal === 'number' && Number.isFinite(innerTotal)) {
      return Math.max(0, Math.floor(innerTotal));
    }
  }
  return itemsLength;
}

function isAdminFeedAnnouncement(n: EventNotification): boolean {
  const t = (n.feedItemType ?? 'admin').toLowerCase();
  return t === 'admin';
}

function paginateLocalAnnouncements(
  items: EventNotification[],
  params: ListEventAnnouncementsParams
): ListEventAnnouncementsResult {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  const start = (page - 1) * limit;
  return {
    items: items.slice(start, start + limit),
    total: items.length,
  };
}

function mapEventTypeFromApi(raw: string | undefined): EventType {
  const s = (raw ?? '').toLowerCase().trim();
  if (s === 'admin') return 'admin';
  if (s === 'sports' || s === 'sport' || s === 'event' || s === 'events') return 'sports';
  if (s === 'system') return 'system';
  if (s === 'promotional' || s === 'promotion' || s === 'promo') return 'promotional';
  return 'other';
}

function mapCategoryToEventType(category: string | undefined): EventType {
  const c = (category ?? '').toUpperCase();
  if (c === 'ADMIN') return 'admin';
  if (c === 'SYSTEM') return 'system';
  if (c.includes('PROMO')) return 'promotional';
  if (c === 'EVENT' || c.includes('SPORT')) return 'sports';
  return 'other';
}

function pickScheduledSendCount(row: EventAnnouncementApiRow): number | undefined {
  const n: unknown =
    row.scheduledSendCount ??
    row.scheduled_send_count ??
    row.sendCount ??
    row.send_count;
  if (typeof n === 'number' && Number.isFinite(n) && n >= 1) return Math.floor(n);
  if (typeof n === 'string' && /^\d+$/.test(n.trim())) {
    const v = Number.parseInt(n, 10);
    if (v >= 1) return v;
  }
  return undefined;
}

function pickScheduledSendAt(row: EventAnnouncementApiRow): string | undefined {
  const s = row.scheduledSendAt ?? row.scheduled_send_at ?? row.sendAt ?? row.send_at;
  if (typeof s === 'string' && s.trim()) return s.trim();
  return undefined;
}

function coerceScheduleInstant(item: unknown): string | undefined {
  if (typeof item === 'string' && item.trim()) return item.trim();
  if (isRecord(item)) {
    const keys = ['at', 'sendAt', 'send_at', 'time', 'scheduledAt', 'scheduled_at', 'iso', 'datetime'];
    for (const k of keys) {
      const v = item[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return undefined;
}

function normalizeScheduledTimeStrings(raw: unknown): string[] | undefined {
  if (Array.isArray(raw)) {
    const out: string[] = [];
    for (const item of raw) {
      const s = coerceScheduleInstant(item);
      if (s) out.push(s);
    }
    return out.length ? out : undefined;
  }
  return undefined;
}

/** Coerce a single API field (string JSON array or array value) into normalized ISO-ish instants. */
function coerceScheduleFieldValue(v: unknown): string[] | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t.length > 0 && t.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(t);
        return normalizeScheduledTimeStrings(parsed);
      } catch {
        return undefined;
      }
    }
    return undefined;
  }
  return normalizeScheduledTimeStrings(v);
}

function pickScheduledSendTimes(row: EventAnnouncementApiRow): string[] | undefined {
  const fromRow =
    coerceScheduleFieldValue(row.pushScheduleTimes) ??
    coerceScheduleFieldValue(row.push_schedule_times) ??
    coerceScheduleFieldValue(row.scheduledSendTimes) ??
    coerceScheduleFieldValue(row.scheduled_send_times) ??
    coerceScheduleFieldValue(row.scheduledSends);
  if (fromRow?.length) return fromRow;

  const legacy = pickScheduledSendAt(row);
  return legacy ? [legacy] : undefined;
}

function appendScheduleToCreate(body: Record<string, unknown>, payload: CreateNotificationPayload): void {
  const times = (payload.scheduledSendTimes ?? [])
    .map((t) => (typeof t === 'string' ? t.trim() : ''))
    .filter(Boolean);
  if (times.length === 0) return;
  const c = Math.min(50, Math.max(1, times.length));
  const slice = times.slice(0, c);
  body.scheduledSendCount = slice.length;
  body.scheduledSendTimes = slice;
  body.pushScheduleTimes = slice;
}

function buildApiCreateBody(payload: CreateNotificationPayload): Record<string, unknown> {
  const targetAll = payload.targetAll ?? true;
  const body: Record<string, unknown> = {
    title: payload.title.trim(),
    message: payload.description.trim(),
    type: mapEventTypeToApiType(payload.eventType),
    isRead: payload.isRead ?? false,
    audienceScope: targetAll ? 'ALL' : 'ZIP_PREFIX_RANGE',
    feedItemType: API_FEED_ITEM_TYPE_ADMIN,
  };
  if (targetAll === false) {
    const from = payload.zipRange?.zipFrom?.trim() ?? '';
    const to = payload.zipRange?.zipTo?.trim() ?? '';
    if (from && to) {
      body.zipPrefixFrom = from;
      body.zipPrefixTo = to;
    }
  }
  appendScheduleToCreate(body, payload);
  return body;
}

function assignScheduledTimesToPatch(target: Record<string, unknown>, times: string[] | undefined): void {
  if (times !== undefined) {
    const trimmed = times.map((t) => (typeof t === 'string' ? t.trim() : '')).filter(Boolean);
    if (trimmed.length === 0) return;
    const slice = trimmed.slice(0, 50);
    target.scheduledSendCount = slice.length;
    target.scheduledSendTimes = slice;
    target.pushScheduleTimes = slice;
  }
}

function buildApiPatchBody(payload: UpdateNotificationPayload): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (payload.title !== undefined) patch.title = payload.title.trim();
  if (payload.description !== undefined) patch.message = payload.description.trim();
  if (payload.isRead !== undefined) patch.isRead = payload.isRead;
  if (payload.eventType !== undefined) {
    patch.type = mapEventTypeToApiType(payload.eventType);
  }
  if (payload.targetAll !== undefined) {
    patch.audienceScope = payload.targetAll ? 'ALL' : 'ZIP_PREFIX_RANGE';
  }
  if (payload.zipRange !== undefined) {
    if (payload.zipRange?.zipFrom && payload.zipRange?.zipTo) {
      patch.zipPrefixFrom = payload.zipRange.zipFrom.trim();
      patch.zipPrefixTo = payload.zipRange.zipTo.trim();
    }
  }
  assignScheduledTimesToPatch(patch, payload.scheduledSendTimes);
  return patch;
}

function isAnnouncementApiRowShape(obj: object): obj is EventAnnouncementApiRow {
  return '_id' in obj || 'id' in obj;
}

function extractSingleAnnouncementRow(data: unknown): EventAnnouncementApiRow | null {
  if (!isRecord(data)) return null;
  if (data.success === false) {
    throw new Error(typeof data.message === 'string' ? data.message : 'Request failed');
  }
  const inner = data.data ?? data.announcement ?? data.item;
  if (inner != null && typeof inner === 'object' && isAnnouncementApiRowShape(inner)) {
    return inner;
  }
  if (isAnnouncementApiRowShape(data)) return data;
  return null;
}

function zipRangeFromValues(from: unknown, to: unknown): ZipRange | undefined {
  if (from == null || to == null) return undefined;
  return { zipFrom: String(from), zipTo: String(to) };
}

function pickZipRange(row: EventAnnouncementApiRow): ZipRange | undefined {
  const fromTopLevel = zipRangeFromValues(row.zipRange?.zipFrom, row.zipRange?.zipTo);
  if (fromTopLevel) return fromTopLevel;

  const nested = row.zip_range;
  if (nested) {
    const fromNested = zipRangeFromValues(nested.zipFrom ?? nested.zip_from, nested.zipTo ?? nested.zip_to);
    if (fromNested) return fromNested;
  }

  const fromPrefix = zipRangeFromValues(
    row.zipPrefixFrom ?? row.zip_prefix_from,
    row.zipPrefixTo ?? row.zip_prefix_to
  );
  if (fromPrefix) return fromPrefix;

  return zipRangeFromValues(row.zip3From ?? row.zip_from, row.zip3To ?? row.zip_to);
}

function pickFeedItemType(row: EventAnnouncementApiRow): string | undefined {
  const raw = row.feedItemType ?? row.feed_item_type;
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw.trim().toLowerCase();
  }
  return undefined;
}

function pickNotificationBodyText(row: EventAnnouncementApiRow): string {
  return (
    row.notificationBody ??
    row.notification_body ??
    row.message ??
    row.description ??
    row.body ??
    row.summary ??
    ''
  ).trim();
}

function resolveTargetAllFromApiRow(
  row: EventAnnouncementApiRow,
  zipRange: ZipRange | undefined,
  audienceAll: boolean
): boolean {
  if (typeof row.targetAll === 'boolean') return row.targetAll;
  if (typeof row.target_all === 'boolean') return row.target_all;
  return audienceAll || zipRange === undefined;
}

function mergeScheduledSendTimes(row: EventAnnouncementApiRow): string[] | undefined {
  let scheduledSendTimes = pickScheduledSendTimes(row);
  if (scheduledSendTimes?.length) return scheduledSendTimes;
  const legacy = pickScheduledSendAt(row);
  return legacy ? [legacy] : undefined;
}

function resolveEventTypeForRow(row: EventAnnouncementApiRow, eventTypeRaw: unknown): EventType {
  if (typeof eventTypeRaw === 'string' && eventTypeRaw.trim() !== '') {
    const trimmed = eventTypeRaw.trim();
    const fromSlug = mapEventTypeFromApi(trimmed);
    if (fromSlug === 'other') {
      return mapCategoryToEventType(trimmed);
    }
    return fromSlug;
  }
  return mapCategoryToEventType(row.category);
}

function pickAnnouncementIdFromRow(row: EventAnnouncementApiRow): string {
  const idTrimmed = String(row._id ?? row.id ?? '').trim();
  if (idTrimmed === '') {
    return generateId();
  }
  return idTrimmed;
}

function pickNotificationTitleFromRow(row: EventAnnouncementApiRow): string {
  const titleTrimmed = (
    row.notificationTitle ??
    row.notification_title ??
    row.title ??
    ''
  ).trim();
  if (titleTrimmed === '') {
    return '(Untitled)';
  }
  return titleTrimmed;
}

function pickOptionalPushCopyFromRow(row: EventAnnouncementApiRow): { pushTitle?: string; pushBody?: string } {
  const pushTitleTrimmed = (row.pushTitle ?? row.push_title ?? '').trim();
  const pushBodyTrimmed = (row.pushBody ?? row.push_body ?? '').trim();
  return {
    ...(pushTitleTrimmed === '' ? {} : { pushTitle: pushTitleTrimmed }),
    ...(pushBodyTrimmed === '' ? {} : { pushBody: pushBodyTrimmed }),
  };
}

function computeAudienceAllFlag(row: EventAnnouncementApiRow): boolean {
  const audience = (row.audienceScope ?? row.audience_scope ?? '').toString().toUpperCase();
  return audience === 'ALL' || audience === 'EVERYONE' || audience === 'GLOBAL';
}

function resolveTargetAllAndZipFromRow(row: EventAnnouncementApiRow): { targetAll: boolean; zipRange?: ZipRange } {
  const zipRange = pickZipRange(row);
  const audienceAll = computeAudienceAllFlag(row);
  const targetAllFromApi = resolveTargetAllFromApiRow(row, zipRange, audienceAll);
  const targetAll = zipRange ? false : targetAllFromApi;
  return { targetAll, zipRange: targetAll ? undefined : zipRange };
}

function pickCreatedUpdatedFromRow(row: EventAnnouncementApiRow, scheduledSendTimes: string[] | undefined): {
  createdAt: string;
  updatedAt: string;
} {
  const createdAt =
    row.createdAt ?? row.created_at ?? scheduledSendTimes?.[0] ?? new Date().toISOString();
  const updatedAt = row.updatedAt ?? row.updated_at ?? createdAt;
  return { createdAt, updatedAt };
}

function mapApiRowToEventNotification(row: EventAnnouncementApiRow): EventNotification {
  const id = pickAnnouncementIdFromRow(row);
  const title = pickNotificationTitleFromRow(row);
  const description = pickNotificationBodyText(row);
  const { pushTitle, pushBody } = pickOptionalPushCopyFromRow(row);
  const eventTypeRaw = row.type ?? row.eventType ?? row.event_type ?? row.category;
  const isRead = row.isRead ?? row.is_read ?? false;
  const { targetAll, zipRange } = resolveTargetAllAndZipFromRow(row);
  const pushSentAt = row.pushSentAt ?? row.push_sent_at;
  const scheduledSendTimes = mergeScheduledSendTimes(row);
  const scheduledSendCount = scheduledSendTimes?.length ?? pickScheduledSendCount(row);
  const { createdAt, updatedAt } = pickCreatedUpdatedFromRow(row, scheduledSendTimes);
  const eventType = resolveEventTypeForRow(row, eventTypeRaw);
  const feedItemType = pickFeedItemType(row);

  return normalizeNotification({
    id,
    notificationTitle: title,
    notificationBody: description,
    pushTitle,
    pushBody,
    isRead: Boolean(isRead),
    eventType,
    ...(feedItemType ? { feedItemType } : {}),
    ...(scheduledSendCount !== undefined && scheduledSendCount >= 1 ? { scheduledSendCount } : {}),
    ...(scheduledSendTimes?.length ? { scheduledSendTimes } : {}),
    targetAll,
    zipRange,
    pushSentAt,
    createdAt,
    updatedAt,
  });
}

async function listFromApi(params: ListEventAnnouncementsParams = {}): Promise<ListEventAnnouncementsResult> {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  const search = params.search?.trim() ?? '';
  const feedItemType = params.feedItemType ?? API_FEED_ITEM_TYPE_ADMIN;
  const response = await axios.get<unknown>(API_BASE_URL, {
    params: {
      page,
      limit,
      feedItemType,
      ...(search !== '' ? { search } : {}),
    },
  });
  const payload = response.data;
  const rows = extractAnnouncementRows(payload);
  const items = rows.map(mapApiRowToEventNotification).filter(isAdminFeedAnnouncement);
  const total = extractAnnouncementTotal(payload, items.length);
  return { items, total };
}

async function list(params: ListEventAnnouncementsParams = {}): Promise<ListEventAnnouncementsResult> {
  try {
    return await listFromApi(params);
  } catch {
    const local = load()
      .filter(isAdminFeedAnnouncement)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return paginateLocalAnnouncements(local, params);
  }
}

async function getById(id: string): Promise<EventNotification | null> {
  const fromLocal = load().find((n) => n.id === id) ?? null;
  try {
    const response = await axios.get<unknown>(`${API_BASE_URL}/${id}`);
    const row = extractSingleAnnouncementRow(response.data);
    if (row) return mapApiRowToEventNotification(row);
  } catch {
    /* not found or server error */
  }
  return fromLocal;
}

async function create(payload: CreateNotificationPayload): Promise<EventNotification> {
  const body = buildApiCreateBody(payload);
  const response = await axios.post<unknown>(API_BASE_URL, body);
  const row = extractSingleAnnouncementRow(response.data);
  if (row) {
    return mapApiRowToEventNotification(row);
  }
  throw new Error('Create succeeded but the response did not include an announcement.');
}

async function update(id: string, payload: UpdateNotificationPayload): Promise<EventNotification> {
  const patch = buildApiPatchBody(payload);
  if (Object.keys(patch).length === 0) {
    throw new Error('Nothing to update');
  }
  const body = { announcementId: id, ...patch };
  const response = await axios.put<unknown>(API_BASE_URL, body);
  const row = extractSingleAnnouncementRow(response.data);
  if (row) return mapApiRowToEventNotification(row);
  const getRes = await axios.get<unknown>(`${API_BASE_URL}/${id}`).catch(() => null);
  if (getRes) {
    const row2 = extractSingleAnnouncementRow(getRes.data);
    if (row2) return mapApiRowToEventNotification(row2);
  }
  throw new Error('Update succeeded but the response could not be parsed.');
}

/** Soft-remove via `PUT /event-announcements` + `validateUpdateEventAnnouncement` — `deleted: true` (restore with `deleted: false`). */
async function remove(id: string): Promise<void> {
  await axios.put<unknown>(API_BASE_URL, { announcementId: id, deleted: true });
  save(load().filter((n) => n.id !== id));
}

function resolvePushHistoryCopy(
  payload: SendPushPayload,
  local: EventNotification | null
): { title: string; bodyPreview: string; zipRange?: ZipRange } {
  // Prefer main announcement copy (same as grid) so admins see "Test — Big game Saturday", not push-only overrides.
  const titleCandidate =
    payload.title?.trim() ??
    local?.notificationTitle?.trim() ??
    local?.pushTitle?.trim() ??
    '';
  const title = titleCandidate === '' ? '(Untitled)' : titleCandidate;
  const bodySource =
    payload.bodyPreview?.trim() ??
    local?.notificationBody?.trim() ??
    local?.pushBody?.trim() ??
    '';
  const bodyPreview = bodySource.slice(0, 160);
  const zipRange = payload.targetAll ? undefined : payload.zipRange ?? local?.zipRange;
  return { title, bodyPreview, zipRange };
}

async function sendPush(payload: SendPushPayload): Promise<void> {
  const announcementId = payload.notificationId.trim();

  const items = load();
  const idx = items.findIndex((n) => n.id === payload.notificationId);
  const local = idx >= 0 ? items[idx] : null;
  const sentAt = new Date().toISOString();
  const { title, bodyPreview, zipRange } = resolvePushHistoryCopy(payload, local);

  // API contract: { announcementId, audience } — audience mirrors announcement `audienceScope` (e.g. ALL).
  const audience = payload.targetAll ? 'ALL' : 'ZIP_PREFIX_RANGE';
  const requestBody: Record<string, unknown> = {
    announcementId,
    audience,
  };
  if (audience === 'ZIP_PREFIX_RANGE') {
    const from = zipRange?.zipFrom?.trim();
    const to = zipRange?.zipTo?.trim();
    if (from && to) {
      requestBody.zipPrefixFrom = from;
      requestBody.zipPrefixTo = to;
    } else {
      throw new Error('ZIP prefix From and To are required when audience is not ALL.');
    }
  }

  await axios.post(`${API_BASE_URL}/send-push`, requestBody);

  if (idx >= 0) {
    items[idx] = { ...items[idx], pushSentAt: sentAt, updatedAt: sentAt };
    save(items);
  }

  if (globalThis.window) {
    const entry: PushHistoryEntry = {
      id: generateId(),
      notificationId: payload.notificationId,
      title,
      bodyPreview,
      sentAt,
      targetAll: payload.targetAll,
      zipRange,
    };
    globalThis.window.dispatchEvent(new CustomEvent(ADMIN_PUSH_SENT, { detail: { entry } }));
  }
}

const notificationsService = {
  list,
  getById,
  create,
  update,
  remove,
  sendPush,
};

export default notificationsService;
